import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const flyers = [{
  id: 1,
  title: 'Deep Dive Bible Study',
  image: 'https://cdn.magicpatterns.com/uploads/s6gFw2wgQMMeputKxJemsQ/WhatsApp_Image_2026-03-13_at_7.30.19_PM.jpg'
}, {
  id: 2,
  title: 'Communion Service',
  image: 'https://cdn.magicpatterns.com/uploads/g2FLwf762fmrFFxfL8vcUk/WhatsApp_Image_2026-03-13_at_7.31.25_PM.jpg'
}, {
  id: 3,
  title: 'Triple-W When We Worship',
  image: 'https://cdn.magicpatterns.com/uploads/4cM78r9dirWzv2hFaMjAik/WhatsApp_Image_2026-03-13_at_7.31.09_PM.jpg'
}, {
  id: 4,
  title: 'Impact Wednesday Revival Service',
  image: 'https://cdn.magicpatterns.com/uploads/i7qsKfq22qq127hrmC3kTK/WhatsApp_Image_2026-03-13_at_7.31.00_PM.jpg'
}, {
  id: 5,
  title: 'Ladies of Excellence Tarry Night',
  image: 'https://cdn.magicpatterns.com/uploads/u3DoDsHQ9gzpBgDTy1eFPq/WhatsApp_Image_2026-03-13_at_7.30.31_PM.jpg'
}];
export function ServiceFlyers() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);
  const scrollBy = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('.flyer-card')?.clientWidth || 300;
    const gap = 24;
    const scrollAmount = (cardWidth + gap) * (direction === 'left' ? -1 : 1);
    el.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }, []);
  // Auto-scroll
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      // If we've reached the end, scroll back to start
      if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 10) {
        el.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else {
        scrollBy('right');
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [isPaused, scrollBy]);
  // Update button states on scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons, {
      passive: true
    });
    updateScrollButtons();
    return () => el.removeEventListener('scroll', updateScrollButtons);
  }, [updateScrollButtons]);
  return <section className="py-20 bg-[#0A1F44] overflow-hidden" data-id="element-62">
      <div className="container-custom" data-id="element-63">
        {/* Header with title and arrows */}
        <div className="flex items-end justify-between mb-10" data-id="element-64">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-65">
            <span className="block text-sm font-bold tracking-widest uppercase mb-3 text-[#C8102E]" data-id="element-66">
              Join Us
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white leading-tight" data-id="element-67">
              Our Weekly Services
            </h2>
            <p className="text-white/60 mt-2 text-sm md:text-base max-w-md" data-id="element-68">
              Explore our services and find the right one for you.
            </p>
          </motion.div>

          {/* Navigation Arrows */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="flex items-center gap-3 shrink-0 ml-4" data-id="element-69">
            <button onClick={() => scrollBy('left')} disabled={!canScrollLeft} className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${canScrollLeft ? 'border-white/40 text-white hover:bg-white hover:text-[#0A1F44]' : 'border-white/15 text-white/25 cursor-not-allowed'}`} aria-label="Previous" data-id="element-70">
              <ChevronLeft className="w-5 h-5" data-id="element-71" />
            </button>
            <button onClick={() => scrollBy('right')} disabled={!canScrollRight} className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${canScrollRight ? 'border-white/40 text-white hover:bg-white hover:text-[#0A1F44]' : 'border-white/15 text-white/25 cursor-not-allowed'}`} aria-label="Next" data-id="element-72">
              <ChevronRight className="w-5 h-5" data-id="element-73" />
            </button>
          </motion.div>
        </div>

        {/* Carousel */}
        <div ref={scrollRef} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onTouchStart={() => setIsPaused(true)} onTouchEnd={() => setTimeout(() => setIsPaused(false), 5000)} className="flex overflow-x-auto snap-x snap-mandatory gap-5 md:gap-6 pb-4 -mx-4 px-4 md:mx-0 md:px-0" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-74">
          <style data-id="element-75">{`
            .flyer-scroll-container::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {flyers.map((flyer, index) => <motion.div key={flyer.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="flyer-card w-[80vw] sm:w-[60vw] md:w-[42vw] lg:w-[30vw] max-w-[420px] snap-start shrink-0" data-id="element-76">
              <div className="rounded-lg overflow-hidden shadow-xl aspect-video bg-[#163A70] ring-1 ring-white/10" data-id="element-77">
                <img src={flyer.image} alt={flyer.title} className="w-full h-full object-cover object-top" data-id="element-78" />
              </div>
              <h3 className="text-white/90 font-medium text-sm md:text-base mt-3 leading-snug" data-id="element-79">
                {flyer.title}
              </h3>
            </motion.div>)}
        </div>
      </div>
    </section>;
}